import React from 'react';
import { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { FOUNDATION_SURVEY_SECTION_CONFIG, getFoundationSurveyDefaultText } from './sections.config';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { DeclarativeLayoutRenderer, DeclarativeLayoutConfig, FieldConfig } from './DeclarativeLayoutRenderer';

interface FoundationSurveySectionProps {
  magicReportId: string;
}

const FoundationSurveySectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const commentsField = getCommentsField(data, FOUNDATION_SURVEY_SECTION_CONFIG.config);

  // toggle logic and default text handling
  const handleStatusChange = (fieldName: 'FormworkSet' | 'GotSurvey' | 'BankDeclined', newValue: 'Yes' | 'No') => {
    const formworkField = getField('FormworkSet');
    const gotSurveyField = getField('GotSurvey');
    const bankDeclinedField = getField('BankDeclined');
    const contentField = getField('FoundationSurveyContent');

    let nextFormwork = formworkField?.value === 'Yes' ? 'Yes' : 'No';
    let nextGotSurvey = gotSurveyField?.value === 'Yes' ? 'Yes' : 'No';
    let nextBankDeclined = bankDeclinedField?.value === 'Yes' ? 'Yes' : 'No';

    if (fieldName === 'FormworkSet') {
      nextFormwork = newValue;
      nextGotSurvey = 'No';
      nextBankDeclined = 'No';
    } else if (fieldName === 'GotSurvey') {
      nextGotSurvey = newValue;
      if (newValue === 'Yes') nextBankDeclined = 'No';
    } else if (fieldName === 'BankDeclined') {
      nextBankDeclined = newValue;
      if (newValue === 'Yes') nextGotSurvey = 'No';
    }

    handleFieldChange('FormworkSet', { value: nextFormwork });
    handleFieldChange('GotSurvey', { value: nextGotSurvey });
    handleFieldChange('BankDeclined', { value: nextBankDeclined });

    const defaultText = getFoundationSurveyDefaultText(nextFormwork, nextGotSurvey, nextBankDeclined);
    if (defaultText && contentField?.value !== defaultText) {
      handleFieldChange('FoundationSurveyContent', { value: defaultText });
    }
  };

  const handleDeleteField = (fieldName: string) => {
    handleFieldChange(fieldName, { value: '', addToActionItems: false });
  };

  // dynamic layout
  const formworkField = getField('FormworkSet');
  const showFollowUps = formworkField?.value === 'Yes';

  const singleColumn: FieldConfig[] = [
    {
      fieldName: 'FormworkSet',
      type: 'select',
      label: 'Formwork Set?',
      options: ['Yes', 'No'],
      showBookmark: false,
      inlineLayout: true,
    },
    ...(showFollowUps
      ? ([
          {
            fieldName: 'GotSurvey',
            type: 'select',
            label: 'Got Survey?',
            options: ['Yes', 'No'],
            showBookmark: false,
            inlineLayout: true,
          },
          {
            fieldName: 'BankDeclined',
            type: 'select',
            label: 'Bank Declined?',
            options: ['Yes', 'No'],
            showBookmark: false,
            inlineLayout: true,
          },
        ] satisfies FieldConfig[])
      : []),
    {
      fieldName: 'FoundationSurveyContent',
      type: 'text',
      label: '',
    },
  ];

  const layout: DeclarativeLayoutConfig = { singleColumn };

  return (
    <>
      <DeclarativeLayoutRenderer
        layout={layout}
        getField={getField}
        handleFieldChange={(fieldName, updates) => {
          if (['FormworkSet', 'GotSurvey', 'BankDeclined'].includes(fieldName)) {
            if (typeof updates.value === 'string' && updates.value !== '') {
              // Treat empty-string (no selection) as "do nothing" to avoid an unselected state
              handleStatusChange(fieldName as any, updates.value as 'Yes' | 'No');
            }
            // Still allow other updates (e.g. bookmarks) that don't touch value
            if (updates.value === undefined) {
              handleFieldChange(fieldName, updates);
            }
          } else {
            handleFieldChange(fieldName, updates);
          }
        }}
        onDeleteField={handleDeleteField}
        onBook={onBook}
      />

      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange(commentsField.fieldName, updated)}
          title='Comments'
          onBook={onBook}
        />
      )}
    </>
  );
};

export const FoundationSurveySection: React.FC<FoundationSurveySectionProps> = ({ magicReportId }) => (
  <MagicReportSection
    magicReportId={magicReportId}
    sectionConfig={FOUNDATION_SURVEY_SECTION_CONFIG}
    customization={{
      renderContent: (data, handleFieldChange, getField, onBook) => (
        <FoundationSurveySectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
      ),
    }}
  />
);
